import React, { useState, useEffect } from "react";
import { showToast } from "../../../../utils/toast";
import {
  getPermitTBTAttendance,
  savePermitTBTAttendance,
  getProjectDetailsById,
} from "../../../../api";
import { resolveActiveProjectId } from "../../../../utils/UserUtils";
import { resolveMediaUrl } from "../../../../lib/utils";
import PermitSignatureModal from "../utils/PermitSignatureModal";
import {
  PermitSignatureCard,
  SignatureViewModal,
  buildTrainerSignatureUser,
} from "../utils/PermitSignatureCard";

const STATIC_TBT_FORMAT_NO = "ADL-OH&S-F003";
const STATIC_TBT_REVISION_NO = "R0";
const STATIC_TBT_ISSUED_DATE = "1st September 25";

const todayYYYYMMDD = () => {
  const d = new Date();
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

const formatToYYYYMMDD = (val) => {
  if (!val) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
    const [d, m, y] = val.split("/");
    return `${y}-${m}-${d}`;
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(val)) {
    const [d, m, y] = val.split("-");
    return `${y}-${m}-${d}`;
  }
  return val;
};

const formatToDDMMYYYY = (val) => {
  if (!val) return "";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) return val;
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [y, m, d] = val.split("-");
    return `${d}/${m}/${y}`;
  }
  return val;
};

const DEFAULT_TBT_SHEET = {
  topic: "",
  format_no: STATIC_TBT_FORMAT_NO,
  revision_no: STATIC_TBT_REVISION_NO,
  issued_date_text: STATIC_TBT_ISSUED_DATE,
  date_of_training: todayYYYYMMDD(),
  project_name: "",
  training_conducted_by_name: "",
  training_conducted_by_sign: "",
};

const dataUrlToFile = (dataUrl, filename) => {
  if (!dataUrl || !dataUrl.startsWith("data:")) return null;
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

const normalizeStatus = (value) =>
  String(value || "").trim().toLowerCase();

const isPermitSubmittedOrBeyond = (permit) => {
  if (permit?.submitted_at) return true;

  const status = normalizeStatus(permit?.current_status);

  if (!status) return false;

  return status !== "draft";
};

export default function TbtAttendanceModal({
  isOpen,
  onClose,
  permit,
  mode = "api", // "api" | "local"
  localRows = [],
  setLocalRows = null,
  localSheet = null,
  setLocalSheet = null,
  firstQuestionId = null,
  readonly = false,
  projectId = null,
}) {
  const [sheet, setSheet] = useState(DEFAULT_TBT_SHEET);
  const [tbtRows, setTbtRows] = useState(
    localRows?.length > 0
      ? localRows
      : [
          {
            person_name: "",
            contractor_name: "",
            designation: "",
            signature: "",
          },
        ]
  );

  const [loadingTbt, setLoadingTbt] = useState(false);
  const [savingTbt, setSavingTbt] = useState(false);

  const [tbtSignatureModalOpen, setTbtSignatureModalOpen] = useState(false);
  const [activeTbtSignatureIndex, setActiveTbtSignatureIndex] = useState(null);
  const [signatureUser, setSignatureUser] = useState(null);

  const trainerUser = React.useMemo(
    () => buildTrainerSignatureUser(permit, sheet),
    [permit, sheet]
  );

  const isTbtLocked = React.useMemo(() => {
    if (readonly) return true;
    if (mode === "local") return false;
    if (isPermitSubmittedOrBeyond(permit)) return true;
    return false;
  }, [readonly, mode, permit]);

  const resolveProjectName = (incomingName) => {
    if (
      incomingName &&
      String(incomingName).trim() &&
      !String(incomingName).trim().startsWith("Project ")
    ) {
      return String(incomingName).trim();
    }

    // 1. Check permit object
    const permitName =
      permit?.form_data?.project_name ||
      permit?.form_data?.project ||
      permit?.project_name ||
      permit?.project?.name;
    if (permitName && String(permitName).trim()) return String(permitName).trim();

    // 2. Check direct localStorage keys
    const directStorage =
      localStorage.getItem("ACTIVE_PROJECT_NAME") ||
      localStorage.getItem("PROJECT_NAME") ||
      localStorage.getItem("project_name") ||
      localStorage.getItem("active_project_name");
    if (directStorage && String(directStorage).trim()) return String(directStorage).trim();

    // 3. Check active project object in localStorage
    try {
      const activeRaw =
        localStorage.getItem("active") || localStorage.getItem("ACTIVE_PROJECT");
      if (activeRaw) {
        const active = JSON.parse(activeRaw);
        const name =
          active?.project_name ||
          active?.projectName ||
          active?.name ||
          active?.title ||
          active?.project?.name ||
          active?.project?.project_name;
        if (name && String(name).trim()) return String(name).trim();
      }
    } catch (_) {}

    // 4. Check projects list in localStorage
    const effPid = String(
      projectId || permit?.project_id || resolveActiveProjectId?.() || ""
    );
    if (effPid) {
      try {
        const listRaw =
          localStorage.getItem("projects") || localStorage.getItem("PROJECTS");
        if (listRaw) {
          const list = JSON.parse(listRaw);
          if (Array.isArray(list)) {
            const found = list.find(
              (x) => String(x?.id ?? x?.project_id ?? x?.pk) === effPid
            );
            const name = found?.name || found?.project_name || found?.title;
            if (name && String(name).trim()) return String(name).trim();
          }
        }
      } catch (_) {}
    }

    // 5. Check USER_DATA
    try {
      const rawUser = localStorage.getItem("USER_DATA");
      if (rawUser) {
        const u = JSON.parse(rawUser);
        const name =
          u?.project_name ||
          u?.projectName ||
          u?.project?.name ||
          (Array.isArray(u?.projects) && u.projects[0]?.name);
        if (name && String(name).trim()) return String(name).trim();
      }
    } catch (_) {}

    if (incomingName && String(incomingName).trim()) return String(incomingName).trim();
    return effPid ? `Project ${effPid}` : "";
  };

  // Widened fallback chain so the trainer's signature is found regardless of
  // which field name the backend returns it under, and — since this field is
  // meant to be "auto-filled" — falls back to the permit creator's own
  // signature if the TBT sheet itself doesn't carry one.
  const resolveTrainerSignature = (loadedSheet) => {
    return (
      loadedSheet?.training_conducted_by_sign_url ||
      loadedSheet?.training_conducted_by_signature_url ||
      loadedSheet?.training_conducted_by_signature ||
      loadedSheet?.training_conducted_by_sign ||
      loadedSheet?.trainer_signature_url ||
      loadedSheet?.trainer_signature ||
      loadedSheet?.conducted_by_signature_url ||
      loadedSheet?.conducted_by_signature ||
      permit?.created_by_signature_url ||
      permit?.created_by_signature ||
      permit?.created_by_sign_url ||
      permit?.created_by_sign ||
      permit?.signature_url ||
      permit?.signature ||
      ""
    );
  };

  const loadTBTAttendance = async () => {
    if (!permit?.id || mode === "local") return;

    setLoadingTbt(true);
    try {
      const res = await getPermitTBTAttendance(permit.id);
      const data = res?.data || {};

      const loadedSheet = data.sheet || {};
      const isLockedSheet = Boolean(readonly || isPermitSubmittedOrBeyond(permit));
      const loadedSavedDate = formatToYYYYMMDD(
        loadedSheet.date_of_training_display || loadedSheet.date_of_training
      );
      setSheet({
        topic: loadedSheet.topic || "",
        format_no: STATIC_TBT_FORMAT_NO,
        revision_no: STATIC_TBT_REVISION_NO,
        issued_date_text: STATIC_TBT_ISSUED_DATE,
        date_of_training: isLockedSheet && loadedSavedDate ? loadedSavedDate : todayYYYYMMDD(),
        project_name: resolveProjectName(loadedSheet.project_name),
        training_conducted_by_name:
          loadedSheet.training_conducted_by_name ||
          permit?.created_by_name ||
          "",
        training_conducted_by_sign: resolveTrainerSignature(loadedSheet),
      });

      const rows = data.rows || [];
      if (rows.length > 0) {
        setTbtRows(
          rows.map((r) => ({
            ...r,
            signature: r.signature_url || r.signature || "",
          }))
        );
      } else {
        setTbtRows([
          {
            person_name: "",
            contractor_name: "",
            designation: "",
            signature: "",
          },
        ]);
      }
    } catch (err) {
      showToast("Failed to load TBT attendance", "error");
    } finally {
      setLoadingTbt(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (mode === "api") {
        loadTBTAttendance();
      } else {
        const initSheet = localSheet || DEFAULT_TBT_SHEET;
        const localSavedDate = formatToYYYYMMDD(initSheet.date_of_training);
        setSheet({
          ...initSheet,
          format_no: STATIC_TBT_FORMAT_NO,
          revision_no: STATIC_TBT_REVISION_NO,
          issued_date_text: STATIC_TBT_ISSUED_DATE,
          date_of_training: isTbtLocked && localSavedDate ? localSavedDate : todayYYYYMMDD(),
          project_name: resolveProjectName(initSheet.project_name),
          training_conducted_by_name:
            initSheet.training_conducted_by_name ||
            permit?.created_by_name ||
            "",
          training_conducted_by_sign:
            initSheet.training_conducted_by_sign || "",
        });
        setTbtRows(
          localRows?.length > 0
            ? localRows
            : [
                {
                  person_name: "",
                  contractor_name: "",
                  designation: "",
                  signature: "",
                },
              ]
        );
      }
    }
  }, [isOpen, permit?.id, mode]);

  useEffect(() => {
    if (!isOpen) return;

    const currentName = sheet.project_name?.trim();
    const isGenericOrEmpty = !currentName || currentName.startsWith("Project ");
    if (!isGenericOrEmpty) return;

    const effPid =
      projectId ||
      permit?.project_id ||
      permit?.form_data?.project_id ||
      resolveActiveProjectId?.() ||
      localStorage.getItem("ACTIVE_PROJECT_ID") ||
      "";

    if (!effPid) return;

    let cancelled = false;
    getProjectDetailsById(effPid)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        const name =
          data?.name ||
          data?.project_name ||
          data?.projectName ||
          data?.title ||
          data?.data?.name ||
          data?.data?.project_name ||
          data?.results?.find?.((p) => String(p.id) === String(effPid))?.name ||
          data?.results?.find?.((p) => String(p.id) === String(effPid))?.project_name;
        if (name && String(name).trim()) {
          const trimmedName = String(name).trim();
          setSheet((prev) => ({ ...prev, project_name: trimmedName }));
          if (typeof setLocalSheet === "function") {
            setLocalSheet((prev) => ({ ...(prev || {}), project_name: trimmedName }));
          }
          try {
            localStorage.setItem("ACTIVE_PROJECT_NAME", trimmedName);
          } catch (_) {}
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId, permit?.project_id, sheet.project_name]);

  const setSheetValue = (key, value) => {
    if (isTbtLocked) return;
    if (key === "date_of_training") return;
    setSheet((prev) => ({ ...prev, [key]: value }));
  };

  const updateTbtRow = (index, key, value) => {
    if (isTbtLocked) return;
    setTbtRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [key]: value } : row))
    );
  };

  const addTbtRow = () => {
    if (isTbtLocked) return;
    setTbtRows((prev) => [
      ...prev,
      { person_name: "", contractor_name: "", designation: "", signature: "" },
    ]);
  };

  const removeTbtRow = (index) => {
    if (isTbtLocked) return;
    setTbtRows((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildTBTPayload = () => {
    const fd = new FormData();
    const qid =
      firstQuestionId ||
      permit?.template_snapshot?.checklist_questions?.[0]?.id ||
      null;

    const sheetData = { ...sheet };
    sheetData.question_id = qid;
    sheetData.format_no = STATIC_TBT_FORMAT_NO;
    sheetData.revision_no = STATIC_TBT_REVISION_NO;
    sheetData.issued_date_text = STATIC_TBT_ISSUED_DATE;
    sheetData.date_of_training = formatToDDMMYYYY(todayYYYYMMDD());
    sheetData.project_name = String(sheet.project_name || "").trim();
    sheetData.topic = String(sheet.topic || "").trim();

    delete sheetData.training_conducted_by_name;
    delete sheetData.training_conducted_by_sign;
    delete sheetData.training_conducted_by_signature;
    delete sheetData.training_conducted_by_signature_url;

    fd.append("sheet", JSON.stringify(sheetData));

    const rowsWithoutBase64 = tbtRows
      .filter((r) => r.person_name || r.contractor_name || r.designation || r.signature)
      .map((row, index) => {
        if (row.signature && row.signature.startsWith("data:")) {
          const signatureFile = dataUrlToFile(
            row.signature,
            `tbt_signature_${index + 1}_${Date.now()}.png`
          );
          fd.append(`signature_${index}`, signatureFile);
        }
        return {
          question_id: qid,
          person_name: row.person_name,
          contractor_name: row.contractor_name,
          designation: row.designation,
        };
      });

    fd.append("rows", JSON.stringify(rowsWithoutBase64));
    return fd;
  };

  const validateTBTBeforeSave = () => {
    if (!String(sheet.topic || "").trim()) {
      showToast("Topic Discussed is required", "error");
      return false;
    }

    if (!sheet.date_of_training || !String(sheet.date_of_training).trim()) {
      showToast("Date of Training is required", "error");
      return false;
    }

    const hasAnyRowData = tbtRows.some(
      (row) =>
        String(row.person_name || "").trim() ||
        String(row.contractor_name || "").trim() ||
        String(row.designation || "").trim() ||
        row.signature
    );

    if (!hasAnyRowData) {
      showToast("Please add at least one attendance row", "error");
      return false;
    }

    return true;
  };

  const handleSaveTBTAttendance = async () => {
    if (isTbtLocked) {
      showToast("TBT attendance cannot be edited after permit submission.", "error");
      return;
    }

    if (!validateTBTBeforeSave()) return;

    if (mode === "local") {
      const updatedSheet = {
        ...sheet,
        date_of_training: todayYYYYMMDD(),
      };
      setLocalRows?.(tbtRows);
      setLocalSheet?.(updatedSheet);
      onClose();
      showToast("TBT attendance saved locally", "success");
      return;
    }

    if (!permit?.id) return;

    setSavingTbt(true);
    try {
      const payload = buildTBTPayload();
      await savePermitTBTAttendance(permit.id, payload);
      showToast("TBT Attendance saved successfully", "success");
      onClose();
    } catch (err) {
      showToast(
        err?.response?.data?.detail || "Failed to save TBT attendance",
        "error"
      );
    } finally {
      setSavingTbt(false);
    }
  };

  const openTbtSignatureModal = (index) => {
    if (isTbtLocked) {
      showToast("TBT attendance signatures cannot be edited after permit submission.", "error");
      return;
    }

    setActiveTbtSignatureIndex(index);
    setTbtSignatureModalOpen(true);
  };

  const handleTbtSignatureSuccess = (signatureDataUrl) => {
    if (isTbtLocked) return;
    if (activeTbtSignatureIndex === null) {
      setSheetValue("training_conducted_by_sign", signatureDataUrl);
    } else {
      updateTbtRow(activeTbtSignatureIndex, "signature", signatureDataUrl);
    }
    setTbtSignatureModalOpen(false);
    setActiveTbtSignatureIndex(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
          <div className="flex-1">
            <h2 className="text-lg font-bold text-slate-900">TBT Attendance</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="overflow-auto p-6 flex-1">
          {loadingTbt ? (
            <div className="flex justify-center p-8 text-slate-500">
              Loading...
            </div>
          ) : (
            <>
              {/* Sheet Details Section */}
              <div className="mb-6 rounded-xl border bg-slate-50 p-5">
                <h3 className="mb-4 text-sm font-bold text-slate-800 border-b pb-2">
                  TBT Header Details
                </h3>
                <div className="space-y-4">
                  {/* Row 1: Format No, Revision No, Issued Date */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">
                        Format No.
                      </label>
                      <input
                        type="text"
                        disabled
                        readOnly
                        value={STATIC_TBT_FORMAT_NO}
                        className="w-full rounded-lg border bg-slate-100 px-3 py-2 text-sm text-slate-700 outline-none cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">
                        Revision No.
                      </label>
                      <input
                        type="text"
                        disabled
                        readOnly
                        value={STATIC_TBT_REVISION_NO}
                        className="w-full rounded-lg border bg-slate-100 px-3 py-2 text-sm text-slate-700 outline-none cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">
                        Issued Date
                      </label>
                      <input
                        type="text"
                        disabled
                        readOnly
                        value={STATIC_TBT_ISSUED_DATE}
                        className="w-full rounded-lg border bg-slate-100 px-3 py-2 text-sm text-slate-700 outline-none cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Row 2: Date of Training (HTML Date Input), Project */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">
                        Date of Training <span className="text-red-500">*</span>
                      </label>
                      <div className="relative cursor-not-allowed">
                        <input
                          type="date"
                          disabled
                          readOnly
                          tabIndex={-1}
                          value={sheet.date_of_training || todayYYYYMMDD()}
                          className="w-full rounded-lg border bg-slate-100 px-3 py-2 text-sm text-slate-700 outline-none cursor-not-allowed select-none pointer-events-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold text-slate-600">
                        Project
                      </label>
                      <input
                        type="text"
                        disabled
                        readOnly
                        value={sheet.project_name || ""}
                        className="w-full rounded-lg border bg-slate-100 px-3 py-2 text-sm text-slate-700 outline-none cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Row 3: Topic Discussed */}
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-slate-600">
                      Topic Discussed <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      disabled={isTbtLocked}
                      readOnly={isTbtLocked}
                      value={sheet.topic || ""}
                      onChange={(e) => setSheetValue("topic", e.target.value)}
                      placeholder="e.g. Height Safety"
                      className="w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:border-orange-500 disabled:opacity-70 disabled:bg-slate-100"
                    />
                  </div>

                  {isTbtLocked && (
                    <div className="mt-4 pt-4 border-t">
                      <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-600">
                        Training Conducted by
                      </label>
                      <div className="max-w-lg">
                        <PermitSignatureCard
                          user={trainerUser}
                          onView={trainerUser?.signature ? setSignatureUser : null}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Attendance Rows Table */}
              <h3 className="mb-3 text-sm font-bold text-slate-800">
                Attendance List
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm min-w-[600px]">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="w-14 border p-2 text-center">SN.</th>
                      <th className="border p-2 text-left">Name of Person</th>
                      <th className="border p-2 text-left">Name of Contractor</th>
                      <th className="border p-2 text-left">Designation</th>
                      <th className="border p-2 text-left">Signature</th>
                      {!isTbtLocked && (
                        <th className="w-24 border p-2 text-center">Action</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {tbtRows.map((row, index) => (
                      <tr key={index}>
                        <td className="border p-2 text-center">{index + 1}</td>
                        <td className="border p-2">
                          <input
                            type="text"
                            disabled={isTbtLocked}
                            readOnly={isTbtLocked}
                            value={row.person_name || ""}
                            onChange={(e) =>
                              updateTbtRow(index, "person_name", e.target.value)
                            }
                            placeholder="Name of person"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:opacity-70 disabled:bg-transparent disabled:border-transparent"
                          />
                        </td>
                        <td className="border p-2">
                          <input
                            type="text"
                            disabled={isTbtLocked}
                            readOnly={isTbtLocked}
                            value={row.contractor_name || ""}
                            onChange={(e) =>
                              updateTbtRow(index, "contractor_name", e.target.value)
                            }
                            placeholder="Name of contractor"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:opacity-70 disabled:bg-transparent disabled:border-transparent"
                          />
                        </td>
                        <td className="border p-2">
                          <input
                            type="text"
                            disabled={isTbtLocked}
                            readOnly={isTbtLocked}
                            value={row.designation || ""}
                            onChange={(e) =>
                              updateTbtRow(index, "designation", e.target.value)
                            }
                            placeholder="Designation"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:opacity-70 disabled:bg-transparent disabled:border-transparent"
                          />
                        </td>
                        <td className="border p-2">
                          <div className="flex items-center gap-3">
                            {row.signature ? (
                              <img
                                src={resolveMediaUrl(row.signature)}
                                alt="TBT Signature"
                                onClick={() =>
                                  window.open(
                                    resolveMediaUrl(row.signature),
                                    "_blank",
                                    "noopener,noreferrer"
                                  )
                                }
                                className="h-10 max-w-[120px] cursor-pointer rounded border object-contain hover:opacity-80"
                                title="Click to view enlarged"
                              />
                            ) : (
                              <span className="text-xs text-slate-400">
                                No signature
                              </span>
                            )}
                            {!isTbtLocked && (
                              <button
                                type="button"
                                onClick={() => openTbtSignatureModal(index)}
                                className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-100"
                              >
                                {row.signature ? "Edit Signature" : "Add Signature"}
                              </button>
                            )}
                          </div>
                        </td>
                        {!isTbtLocked && (
                          <td className="border p-2 text-center">
                            <button
                              type="button"
                              onClick={() => removeTbtRow(index)}
                              disabled={tbtRows.length === 1}
                              className="rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs font-semibold text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {isTbtLocked && tbtRows.length === 0 && (
                      <tr>
                        <td
                          colSpan="5"
                          className="border p-4 text-center text-slate-500"
                        >
                          No TBT attendance added.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {!isTbtLocked && (
                <button
                  type="button"
                  onClick={addTbtRow}
                  className="mt-4 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-100"
                >
                  + Add Row
                </button>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t px-6 py-4 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={savingTbt}
            className="rounded-lg border px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {isTbtLocked ? "Close" : "Cancel"}
          </button>
          {!isTbtLocked && (
            <button
              type="button"
              onClick={handleSaveTBTAttendance}
              disabled={savingTbt || loadingTbt}
              className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50"
            >
              {savingTbt ? "Saving..." : "Save Attendance"}
            </button>
          )}
        </div>
      </div>

      {!isTbtLocked && (
        <PermitSignatureModal
          isOpen={tbtSignatureModalOpen}
          onClose={() => {
            setTbtSignatureModalOpen(false);
            setActiveTbtSignatureIndex(null);
          }}
          onSignatureSuccess={handleTbtSignatureSuccess}
          onSuccess={handleTbtSignatureSuccess}
        />
      )}

      <SignatureViewModal
        open={!!signatureUser}
        user={signatureUser}
        onClose={() => setSignatureUser(null)}
      />
    </div>
  );
}